import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import { Typography, Box, Flex, BoxProps, IconButton, Skeleton } from '@kistech/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as clientsActions, selectors as clientsSelectors, Client } from 'store/clients';
import { KioskControlCenter } from 'components/KioskControlCenter';
import { useStyles } from './styled';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';
import { getSortedList } from 'utils/stringFormat';

const dividerStyles: BoxProps = {
  flexOverride: 'none',
  height: 1,
  bg: 'gray',
  fullWidth: true
};

const ClientSelectView: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
  // ACTION CREATORS AND SELECTORS
  const { setClientId, getClients } = useActions({
    setClientId: clientsActions.setClientId,
    getClients: clientsActions.getClients
  });
  const clients = useSelector(clientsSelectors.selectClients) as Client[];
  const pending = useSelector(clientsSelectors.selectPending);
  const classes = useStyles();

  // ON MOUNT - GET CLIENTS
  React.useEffect(() => {
    getClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ON UPDATE OF CLIENTS - IF CLIENTS ARRAY HAS ONLY ONE ITEM, SELECT THAT CLIENT'S ID AND REDIRECT TO NEXT PAGE (KiosksView)
  React.useEffect(() => {
    if (clients.length === 1) {
      setClientId(clients[0].id);
      props.history.push('/kiosks');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, props.history]);

  // CLIENT CLICK
  const onClientClick = (id: string) => {
    // SELECT THAT CLIENT'S ID AND REDIRECT TO NEXT PAGE (KiosksView)
    setClientId(id);
    props.history.push('/kiosks');
  };

  // IF CLIENTS ARRAY HAS ONLY ONE ITEM, RENDER NULL
  if (clients.length === 1) return null;

  return (
    <Grid container direction="column" className={classes.clientSelectViewContainer}>
      {/* LEFT OF CARD */}
      <Grid item md={12} xl={6} lg={6} className={classes.clientSelectImageBlock}>
        <Flex fullHeight bg="light-gray">
          <KioskControlCenter />
        </Flex>
      </Grid>
      {/* RIGHT OF CARD - LIST OF CLIENTS */}
      <Grid item md={12} xl={6} lg={6} className={classes.clientSelectViewBlock}>
        <MuiBox className={classes.clientSelectHeaderBox}>
          <MuiTypography className={classes.clientSelectHeader}>Select a Client</MuiTypography>
        </MuiBox>
        {/* CLIENTS */}
        <MuiBox className={classes.clientSelectListBox}>
          {!clients.length || pending ? (
            <Flex direction="column" gutter={2} padding="0 10 0 0">
              <Flex noWrap align="center" justify="between">
                <Skeleton height={30} width="80%" />
                <Skeleton variant="circle" height={40} width={40} />
              </Flex>
              <Flex noWrap align="center" justify="between">
                <Skeleton height={30} width="60%" />
                <Skeleton variant="circle" height={40} width={40} />
              </Flex>
              <Flex noWrap align="center" justify="between">
                <Skeleton height={30} width="70%" />
                <Skeleton variant="circle" height={40} width={40} />
              </Flex>
            </Flex>
          ) : (
            getSortedList(clients, 'name').map((client, idx) => (
              <MuiBox key={client.id} className={classes.clientSelectListItemBox}>
                {/* CLIENT ROW */}
                {!idx && <Box {...dividerStyles} />}
                <Grid className={classes.clientSelectNameContainer}>
                  <Box flexOverride="none">
                    <Typography size={24} weight={600}>
                      {client.name}
                    </Typography>
                  </Box>
                  <IconButton
                    appearance="primary"
                    name="next_circle"
                    padding="1"
                    size={34}
                    onClick={() => onClientClick(client.id)}
                    data-testid={`client-${idx}`}
                  />
                </Grid>
                <Box {...dividerStyles} />
              </MuiBox>
            ))
          )}
        </MuiBox>
      </Grid>
    </Grid>
  );
};

export default ClientSelectView;
